import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, MessageSquare, Lightbulb, AlertTriangle, CheckCircle, Clock, XCircle, ChevronLeft, ArrowUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { createTicket, getTickets, addTicketMessage } from '../services/api';
import Loader from './Loader';
import { toast } from 'sonner';

const TICKET_TYPES = [
  { value: 'support', label: 'Destek', icon: MessageSquare, color: 'bg-blue-100 text-blue-600' },
  { value: 'suggestion', label: 'Öneri', icon: Lightbulb, color: 'bg-green-100 text-green-600' },
  { value: 'complaint', label: 'Şikayet', icon: AlertTriangle, color: 'bg-red-100 text-red-600' },
];

const STATUS_CFG = {
  active: { label: 'Aktif', icon: Clock, cls: 'text-orange-500 bg-orange-50' },
  answered: { label: 'Cevaplandı', icon: CheckCircle, cls: 'text-green-600 bg-green-50' },
  pending: { label: 'Beklemede', icon: Clock, cls: 'text-amber-600 bg-amber-50' },
  closed: { label: 'Çözüldü', icon: XCircle, cls: 'text-gray-500 bg-gray-100' },
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Az önce';
  if (diffMins < 60) return `${diffMins} dk önce`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} saat önce`;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const TicketsModal = ({ open, onClose, onCountChange }) => {
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [myTickets, setMyTickets] = useState([]);
  const [view, setView] = useState('list');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState({ type: 'support', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const loadMyTickets = useCallback(async () => {
    try {
      const response = await getTickets();
      setMyTickets(response.data);
    } catch (error) { console.error(error); }
    finally { setLoadingTickets(false); }
  }, []);

  useEffect(() => {
    if (open) { setLoadingTickets(true); setView('list'); setSelectedTicket(null); loadMyTickets(); }
  }, [open, loadMyTickets]);

  const unreadCount = myTickets.filter(t => !t.read_by_user && (t.status === 'answered' || t.admin_response)).length;

  useEffect(() => {
    if (onCountChange) onCountChange(unreadCount);
  }, [unreadCount, onCountChange]);

  useEffect(() => {
    if (view === 'thread' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [view, selectedTicket?.messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }
    setSubmitting(true);
    try {
      await createTicket(formData);
      toast.success('Destek talebiniz gönderildi');
      setFormData({ type: 'support', subject: '', message: '' });
      setView('list');
      loadMyTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bir hata oluştu');
    } finally { setSubmitting(false); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await addTicketMessage(selectedTicket.id, replyText);
      setReplyText('');
      const response = await getTickets();
      setMyTickets(response.data);
      const updated = response.data.find(t => t.id === selectedTicket.id);
      if (updated) setSelectedTicket(updated);
      toast.success('Mesajınız gönderildi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mesaj gönderilemedi');
    } finally { setSending(false); }
  };

  const openThread = (ticket) => {
    setSelectedTicket(ticket);
    setView('thread');
  };

  const getTypeInfo = (type) => TICKET_TYPES.find(t => t.value === type) || TICKET_TYPES[0];

  const getMessages = (ticket) => {
    if (ticket.messages && ticket.messages.length > 0) {
      const hasOriginal = ticket.messages[0]?.text === ticket.message;
      if (!hasOriginal && ticket.message) {
        return [{ sender: 'user', sender_name: ticket.user_name, text: ticket.message, created_at: ticket.created_at }, ...ticket.messages];
      }
      return ticket.messages;
    }
    const msgs = [{ sender: 'user', sender_name: ticket.user_name, text: ticket.message, created_at: ticket.created_at }];
    if (ticket.admin_response) {
      msgs.push({ sender: 'admin', sender_name: 'AgrosVet Destek Ekibi', text: ticket.admin_response, created_at: ticket.updated_at });
    }
    return msgs;
  };

  const canReply = selectedTicket && selectedTicket.status !== 'closed' && selectedTicket.status !== 'pending';

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent data-testid="tickets-modal" className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-4 lg:px-6 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <DialogTitle className="flex items-center justify-between pr-8">
            <div className="flex items-center gap-3">
              {view !== 'list' && (
                <button onClick={() => { setView('list'); setSelectedTicket(null); }} className="p-1 rounded-full hover:bg-gray-100" data-testid="tickets-back-btn">
                  <ChevronLeft size={20} className="text-[#8E8E93]" />
                </button>
              )}
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                <MessageSquare size={18} className="text-blue-500" />
              </div>
              <div>
                <h2 className="text-base lg:text-lg font-bold text-gray-900">
                  {view === 'form' ? 'Yeni Talep' : view === 'thread' ? selectedTicket?.subject : 'Destek Talepleri'}
                </h2>
                <span className="block text-xs text-[#8E8E93] font-normal">
                  {view === 'list' ? `${myTickets.length} talep${unreadCount > 0 ? ` · ${unreadCount} yeni yanıt` : ''}` :
                   view === 'thread' && selectedTicket ? (() => { const s = STATUS_CFG[selectedTicket.status] || STATUS_CFG.active; return s.label; })() : 'Detayları doldurun'}
                </span>
              </div>
            </div>
            {view === 'list' && (
              <Button size="sm" onClick={() => setView('form')} className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-full text-xs shrink-0" data-testid="new-ticket-btn">
                <Send size={12} className="mr-1" /> Yeni Talep
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* LIST VIEW */}
          {view === 'list' && (
            loadingTickets ? (
              <div className="flex items-center justify-center py-12"><Loader /></div>
            ) : myTickets.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare size={40} className="text-[#C7C7CC] mx-auto mb-2" />
                <p className="text-sm text-[#8E8E93]">Henüz destek talebiniz yok</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {myTickets.map(ticket => {
                  const typeInfo = getTypeInfo(ticket.type);
                  const statusCfg = STATUS_CFG[ticket.status] || STATUS_CFG.active;
                  const StatusIcon = statusCfg.icon;
                  const msgs = getMessages(ticket);
                  const lastMsg = msgs[msgs.length - 1];
                  return (
                    <button
                      key={ticket.id}
                      onClick={() => openThread(ticket)}
                      className={`w-full text-left px-4 lg:px-6 py-3 hover:bg-gray-50 transition-colors ${!ticket.read_by_user && ticket.status === 'answered' ? 'bg-blue-50/30' : ''}`}
                      data-testid={`my-ticket-${ticket.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${typeInfo.color}`}>{typeInfo.label}</span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${statusCfg.cls}`}>
                            <StatusIcon size={10} /> {statusCfg.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {!ticket.read_by_user && ticket.status === 'answered' && (
                            <span className="w-2 h-2 rounded-full bg-[#6b6de3]" />
                          )}
                          <span className="text-[10px] text-[#C7C7CC]">{formatTime(ticket.updated_at)}</span>
                        </div>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm truncate">{ticket.subject}</p>
                      {lastMsg && (
                        <p className="text-xs text-[#8E8E93] mt-0.5 truncate">
                          {lastMsg.sender === 'admin' ? 'AgrosVet: ' : ''}{lastMsg.text}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            )
          )}

          {/* NEW TICKET FORM */}
          {view === 'form' && (
            <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-[#8E8E93]">Talep Türü</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger className="h-10 bg-[#F2F2F7] border-0 rounded-xl" data-testid="ticket-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-[#8E8E93]">Konu</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Konu başlığını yazın..."
                  className="h-10 bg-[#F2F2F7] border-0 rounded-xl"
                  required
                  data-testid="ticket-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-[#8E8E93]">Mesaj</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Detaylı açıklama yazın..."
                  className="bg-[#F2F2F7] border-0 rounded-xl min-h-[100px]"
                  required
                  data-testid="ticket-message-input"
                />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="ghost" onClick={() => setView('list')} className="flex-1 rounded-full">{'İ'}ptal</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-[#6b6de3] hover:bg-[#5658c4] rounded-full" data-testid="ticket-submit-btn">
                  {submitting ? 'Gönderiliyor...' : 'Gönder'}
                </Button>
              </div>
            </form>
          )}

          {/* THREAD VIEW */}
          {view === 'thread' && selectedTicket && (
            <div className="flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-3">
                {getMessages(selectedTicket).map((msg, i) => (
                  <div key={i} className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                      msg.sender === 'admin'
                        ? 'bg-[#F2F2F7] text-gray-900 rounded-bl-md'
                        : 'bg-[#6b6de3] text-white rounded-br-md'
                    }`} data-testid={`msg-${i}`}>
                      {msg.sender === 'admin' && (
                        <p className="text-[10px] font-semibold text-[#8E8E93] mb-0.5">AgrosVet Destek Ekibi</p>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender === 'admin' ? 'text-[#C7C7CC]' : 'text-white/60'}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}
        </div>

        {view === 'thread' && canReply && (
          <div className="p-3 lg:px-6 border-t border-gray-100 shrink-0">
            <div className="flex items-center gap-2">
              <Input
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleReply())}
                placeholder="Mesajınızı yazın..."
                className="flex-1 h-10 bg-[#F2F2F7] border-0 rounded-full px-4"
                data-testid="ticket-reply-input"
              />
              <button
                onClick={handleReply}
                disabled={sending || !replyText.trim()}
                className="w-10 h-10 rounded-full bg-[#6b6de3] flex items-center justify-center text-white disabled:opacity-40 shrink-0 hover:bg-[#5658c4] transition-colors"
                data-testid="ticket-reply-send"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </div>
        )}

        {view === 'thread' && !canReply && selectedTicket && (
          <div className="p-3 lg:px-6 border-t border-gray-100 shrink-0 text-center">
            <p className="text-xs text-[#8E8E93]">
              Bu talep {selectedTicket.status === 'closed' ? 'çözüldü olarak işaretlendi' : 'beklemede'}, mesaj eklenemez.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TicketsModal;
